import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { AlertInfo } from '@ad-scripts/presentation/components/common/alert';
import { Body, PageHeader, Row } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import customChannelRecipientResource from 'src/actions/channels/customChannelRecipientResource';
import ConfirmCancelModal from 'src/presentation/components/forms/ConfirmCancelModal';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { FormEditorProps, useForm } from 'src/presentation/components/forms/Form';
import InputField from 'src/presentation/components/forms/InputField';
import { STATUS_PERSISTING, STATUS_SUCCEEDED, useResourceValues } from 'src/presentation/hooks/useResource';
import useDirtyFormConfirmation from 'src/presentation/components/pages/shared/hooks/useDirtyFormConfirmation';
import useRouter from 'src/presentation/hooks/useRouter';
import useOnSaveRecipientIds from './hooks/useOnSaveRecipientIds';

const RecipientIdEditor = ({ errors, item, root }: FormEditorProps) => {
  const {
    match: { params },
  }: { match: { params: { customChannelId: string } } } = useRouter();
  const customChannelId = params.customChannelId;

  const [isComplete, setComplete] = useState<boolean>();

  const { add, update, validate } = customChannelRecipientResource.effects;
  const { resetErrors } = customChannelRecipientResource.actions;

  const { persistingState } = useResourceValues(customChannelRecipientResource);

  const handleSave = useOnSaveRecipientIds();

  const formContext = useForm({
    add,
    defaultItem: { configuration: { customChannelId } },
    errors,
    item,
    onSave: handleSave,
    resetErrors,
    root,
    update,
    validate,
  });

  const { isNew, onClose, onSave } = formContext;

  const { handleCancel, handleStay, isConfirmCancelModalVisible, isModified } = useDirtyFormConfirmation({
    formContext,
    isComplete,
    setComplete,
  });

  useEffect(() => {
    if (persistingState === STATUS_SUCCEEDED) {
      setComplete(true);
    }
  }, [persistingState, setComplete]);

  const recipientIdFieldDescription = 'The address used to map inbound messages to the Customer Profile.';

  return (
    <>
      <Form context={formContext} preventSaveOnEnter>
        <PageHeader
          headerText={`${isNew ? 'Add' : 'Edit'} Entry Point`}
          descriptionText="Assign the internal name and Entry Point Address."
        />
        <Body>
          <Row>
            <InputField
              label="Name"
              description={
                <>
                  The internal name used to easily identify the Channel’s Entry Point
                  <br /> in Gladly.
                </>
              }
              data-aid="name"
              attr="name"
              placeholder="Enter the name"
            />
          </Row>

          <Row>
            <InputField
              label="Entry Point Address"
              description={recipientIdFieldDescription}
              data-aid="address"
              attr="configuration.recipientAddress"
              placeholder="Enter the address"
              disabled={!isNew}
            />
          </Row>

          {isNew && (
            <Row>
              <StyledAlertInfo data-aid="alert-info-heads-up">
                <b>Heads up:&nbsp;</b>
                Before saving, please double check the Entry Point Address. You can only edit and save this field once.
              </StyledAlertInfo>
            </Row>
          )}
        </Body>

        <Footer>
          <Button data-aid="cancel-button" type={Button.Types.TEXT} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            data-aid="save-button"
            disabled={!isModified}
            isLoading={persistingState === STATUS_PERSISTING}
            onClick={onSave}
          >
            Save
          </Button>
        </Footer>
      </Form>
      <ConfirmCancelModal
        name="Recipient ID"
        handleLeave={onClose}
        handleStay={handleStay}
        isModified={isModified && !isComplete}
        isVisible={isConfirmCancelModalVisible}
        customText="If you leave this page, your changes will be discarded."
      />
    </>
  );
};

const StyledAlertInfo = styled(AlertInfo)`
  line-height: 20px;
  > svg {
    margin-top: 5px;
  }
`;

export default RecipientIdEditor;
